import { useState, useContext, useEffect, useCallback } from "react";
import { Box } from "@mui/material";
import type { Table as ArrowTable } from "apache-arrow";

import { ParsedParquetData } from "../../utils/parquet";
import { PivotViewConfig } from "../../entities/pivot_view_config";
import { ChartConfigProp } from "../../entities/chart_config";
import { TransformedCustomViewControlsContainer } from "./controls";
import { TransformedCustomViewViewerContainer } from "./viewer";
import { useParams } from "../../hooks/use_date_pivot_params";
import { VersionedDataContext } from "../../contexts/versioned_data/context";
import { DatePivotParams } from "../../entities/date_pivot_params";

export type BackendExportFn = (excludeDetailRows: boolean) => Promise<void>;

export const TransformedCustomViewContainer = <
  T extends DatePivotParams = DatePivotParams,
>({
  viewControllerConfig,
  defaultConfig,
  viewerComponent,
  chartConfig,
  customChartViewerComponent,
}: {
  viewControllerConfig: PivotViewConfig<T>;
  defaultConfig: T;
  viewerComponent?: React.ComponentType<any>;
  chartConfig?: ChartConfigProp;
  customChartViewerComponent?: React.ComponentType<any>;
}) => {
  const {
    datasetName,
    transformId,
    tableType,
    columnHeaderMap,
    columnFormatters,
  } = viewControllerConfig;

  const { mainRun, comparedRun } = useContext(VersionedDataContext);
  const { loading, viewParams, setViewParams } = useParams(
    defaultConfig,
    datasetName,
    transformId
  );

  const [controllerLoading, setControllerLoading] = useState(false);
  const [isChartView, setIsChartView] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedParquetData | null>(null);
  const [columnSchema, setColumnSchema] = useState<
    Record<
      string,
      {
        dtype: string;
        min?: string;
        max?: string;
        unique_values?: string[];
        value_counts?: Record<string, number>;
      }
    >
  >({});

  // Modify segments only when switching to chart view
  useEffect(() => {
    if (isChartView && viewParams?.segments && viewParams.segments.length > 1) {
      // Only keep the first segment when switching to chart view
      setViewParams({
        ...viewParams,
        segments: [viewParams.segments[0]],
      } as T);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChartView]);

  // Initialize sortConfig as empty array - let getMatrix handle initial sort
  const [sortConfig, setSortConfig] = useState<
    Array<{ key: string; direction: "asc" | "desc"; index: number }>
  >([]);

  // Add expand/collapse state for inventory health
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // In-memory ArrowTable for base64 data that is wrapped as chunked.
  // The viewer sets this when a small dataset arrives as base64; the controls
  // use it for the frontend CSV export path.
  const [inMemoryArrowTable, setInMemoryArrowTable] = useState<ArrowTable | null>(null);

  // Backend CSV export callback (set by the viewer when chunked path is active)
  const [backendExportFn, setBackendExportFn] = useState<BackendExportFn | null>(null);

  // Stable wrapper so the controls component gets a consistent reference
  const handleBackendExport = useCallback(
    async (excludeDetailRows: boolean) => {
      if (backendExportFn) await backendExportFn(excludeDetailRows);
    },
    [backendExportFn]
  );

  // Reset sortConfig when measure changes (for Inventory Health views with measure-based default sorting)
  useEffect(() => {
    if (
      viewControllerConfig.title.startsWith("Inventory Health") &&
      viewParams?.measure
    ) {
      setSortConfig([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewParams?.measure]);

  const lockedSetId = mainRun?.runSettings
    ? `${mainRun.runSettings?.inputId}|${mainRun.runSettings?.settingsNameAndVersion}`
    : null;
  const comparedToSetId = comparedRun?.runSettings
    ? `${comparedRun.runSettings?.inputId}|${comparedRun.runSettings?.settingsNameAndVersion}`
    : null;

  if (loading || !viewParams) {
    return null;
  }

  // Type-safe wrapper for setViewParams that handles the generic type T
  const handleSetViewParams = (params: T | ((prev: T | null) => T | null)) => {
    setViewParams(params as any);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <TransformedCustomViewControlsContainer<T>
        viewControllerConfig={viewControllerConfig}
        controllerLoading={controllerLoading}
        isChartView={isChartView}
        setIsChartView={setIsChartView}
        params={viewParams as T}
        setParams={handleSetViewParams}
        setControllerLoading={setControllerLoading}
        lockedSetId={lockedSetId}
        comparedToSetId={comparedToSetId}
        parsedData={parsedData}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        columnSchema={columnSchema}
        setColumnSchema={setColumnSchema}
        onBackendExport={backendExportFn ? handleBackendExport : undefined}
        inMemoryArrowTable={inMemoryArrowTable}
      />

      <TransformedCustomViewViewerContainer<T>
        viewControllerConfig={viewControllerConfig}
        params={viewParams as T}
        runSettings={mainRun?.runSettings || null}
        comparedToRunSettings={comparedRun?.runSettings || null}
        viewerComponent={viewerComponent as React.ComponentType<any>}
        tableType={tableType || "pivot"}
        columnHeaderMap={columnHeaderMap}
        columnFormatters={columnFormatters}
        parsedData={parsedData}
        setParsedData={setParsedData}
        isChartView={isChartView}
        sortConfig={sortConfig}
        setSortConfig={setSortConfig}
        chartConfig={chartConfig as ChartConfigProp}
        ChartComponent={customChartViewerComponent as React.ComponentType<any>}
        columnSchema={columnSchema}
        expandedGroups={expandedGroups}
        setExpandedGroups={setExpandedGroups}
        setBackendExportFn={setBackendExportFn}
        setInMemoryArrowTable={setInMemoryArrowTable}
      />
    </Box>
  );
};
